import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchImportRulePreview } from '../../../redux/actions/importRules.js';
import { moneyFormatFromCents } from '../../../utilities/moneyFormat';
import Button from '../../components/Button.jsx';
import CategorySelect from '../../components/CategorySelect.jsx';
import RuleValueSelect from '../../components/inputs/RuleValueSelect.jsx';
import { EXTRA_OPTION_GROUPS, NON_CATEGORY_RULES, RULE_NONE, valueOf } from './importCategoryRule.js';
import {
	accountRuleOptions,
	facetOptions,
	findGroupedOption,
	previewParams,
	signHint,
} from './importRulePreview.js';
import { centsToDollars, dollarsToCents } from './importRuleSummary.js';

let editorCount = 0;

/** How long typing pauses before the preview is asked again. */
export const PREVIEW_DELAY = 300;

/** How many of a facet's values are offered as one-tap suggestions under a typed field. */
const QUICK_PICKS = 6;

/**
 * One rule's conditions and outcome, for a new rule (`rule` null) or an existing one.
 *
 * Every condition but the statement matches exactly, so each offers what imports have actually used,
 * narrowed by the others and counted (Import_Domain::rule_preview), and the draft is previewed
 * against those rows as it is written: how many match, the newest few, and whether the sign looks
 * right for the outcome. The account picker draws the budget's account each Monarch name is mapped to
 * (`accountRules`, `availableAccounts`). Saving is `onSave(draft)`, which resolves `{ saved, error }`;
 * a refusal stays here, beside the fields it is about.
 */
export default function ImportRuleEditor({
	rule,
	accountRules,
	availableAccounts,
	availableCategories,
	ruleCount,
	onSave,
	onCancel,
}) {
	const idPrefix = useMemo(() => `import-rule-editor-${++editorCount}`, []);
	const [account, setAccount] = useState(rule ? rule.external_account : '');
	const [category, setCategory] = useState(rule ? rule.external_category : '');
	const [noCategory, setNoCategory] = useState(rule ? !!rule.no_category : false);
	const [merchant, setMerchant] = useState(rule ? rule.external_merchant : '');
	const [statement, setStatement] = useState(rule ? rule.statement_contains : '');
	const [amountText, setAmountText] = useState(rule ? centsToDollars(rule.amount) : '');
	const [choice, setChoice] = useState(rule ? valueOf(rule) : RULE_NONE);
	const [reverseSign, setReverseSign] = useState(rule ? !!rule.reverse_sign : false);
	const [positionText, setPositionText] = useState(rule ? String(rule.position) : '');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	/** A save that succeeds closes the editor before its answer gets back here. */
	const mounted = useRef(true);
	useEffect(() => () => {
		mounted.current = false;
	}, []);

	const amount = dollarsToCents(amountText);
	const isAction = NON_CATEGORY_RULES.includes(choice);
	const assigned = !isAction && typeof choice === 'number' && choice !== RULE_NONE
		? (availableCategories || []).find((candidate) => candidate.id === choice)
		: null;
	/** Bank-record-only and skipped rows have no ledger transaction to put a sign on. */
	const signApplies = !isAction || choice === 'uncategorized';
	const hasCondition = !!(account.trim() || noCategory || category.trim() || merchant.trim() || statement.trim()
		|| amount.cents !== null);

	const params = previewParams({ account, category, noCategory, merchant, statement, amountCents: amount.error ? null : amount.cents });
	const { preview, loading } = useRulePreview(params);
	const facets = (preview && preview.facets) || {};

	const accountOptions = useMemo(
		() => accountRuleOptions(facets.accounts, accountRules, availableAccounts, account),
		[facets.accounts, accountRules, availableAccounts, account]
	);
	const chosenAccount = findGroupedOption(accountOptions, account.trim());
	const hint = signApplies && hasCondition
		? signHint({
			preview,
			category: assigned,
			accountIsLiability: !!(chosenAccount && chosenAccount.identity && chosenAccount.identity.isLiability),
			reverseSign,
		})
		: null;

	const save = () => {
		if(amount.error) {
			setError(amount.error);
			return;
		}
		if(!hasCondition) {
			setError('Give the rule at least one condition.');
			return;
		}
		if(!isAction && !assigned) {
			setError('Choose what these rows become.');
			return;
		}
		const position = positionText.trim() === '' ? null : parseInt(positionText, 10);
		if(position !== null && !(position >= 1)) {
			setError('A position is a number from 1 up.');
			return;
		}
		setError(null);
		setSaving(true);
		const draft = {
			id: rule ? rule.id : null,
			external_account: account.trim(),
			external_category: noCategory ? '' : category.trim(),
			no_category: noCategory,
			external_merchant: merchant.trim(),
			statement_contains: statement.trim(),
			amount: amount.cents,
			action: isAction ? choice : 'assign',
			category_id: isAction ? null : choice,
			reverse_sign: signApplies && reverseSign,
			/** An existing rule's own place is no move; sending it would still be one. */
			position: rule && position === rule.position ? null : position,
		};
		Promise.resolve(onSave(draft)).then((result) => {
			if(!mounted.current) {
				return;
			}
			setSaving(false);
			if(result && !result.saved && result.error) {
				setError(result.error);
			}
		});
	};

	const typedStatement = statement.trim().toLowerCase();
	const statementPicks = (facets.statements || [])
		.filter((entry) => entry.value.toLowerCase().includes(typedStatement) && entry.value !== statement.trim())
		.slice(0, QUICK_PICKS);
	const amountPicks = (facets.amounts || []).filter((entry) => entry.value !== amount.cents).slice(0, QUICK_PICKS);

	return (
		<div className='import-rule-editor'>
			<h6>{rule ? `Edit rule ${rule.position}` : 'New rule'}</h6>
			<p className='grey-text import-rule-editor-note'>
				A row has to match every condition you fill in. Leave one blank to match anything.
			</p>

			<div className='import-rule-fields'>
				<div className='import-rule-select-field'>
					<label className='grey-text' htmlFor={`${idPrefix}-account`}>Account</label>
					<RuleValueSelect
						inputId={`${idPrefix}-account`}
						aria-label='Account'
						options={accountOptions}
						value={account}
						placeholder='Any account'
						onChange={setAccount}
					/>
				</div>

				<div className='import-rule-select-field'>
					<label className='grey-text' htmlFor={`${idPrefix}-category`}>Category in Monarch</label>
					<RuleValueSelect
						inputId={`${idPrefix}-category`}
						aria-label='Category in Monarch'
						options={facetOptions(facets.categories, noCategory ? '' : category)}
						value={noCategory ? '' : category}
						isDisabled={noCategory}
						placeholder={noCategory ? 'Rows with no category' : 'Any category'}
						onChange={setCategory}
					/>
				</div>
				<label className='import-rule-check'>
					<input
						type='checkbox'
						className='filled-in'
						checked={noCategory}
						onChange={(event) => setNoCategory(event.target.checked)}
					/>
					<span>Only rows Monarch gave no category</span>
				</label>

				<div className='import-rule-select-field'>
					<label className='grey-text' htmlFor={`${idPrefix}-merchant`}>Merchant</label>
					<RuleValueSelect
						inputId={`${idPrefix}-merchant`}
						aria-label='Merchant'
						options={facetOptions(facets.merchants, merchant)}
						value={merchant}
						placeholder='Any merchant'
						onChange={setMerchant}
					/>
				</div>

				<label>
					<span className='grey-text'>Statement contains</span>
					<input
						type='text'
						value={statement}
						maxLength={255}
						placeholder='Any statement'
						onChange={(event) => setStatement(event.target.value)}
					/>
					<span className='grey-text import-rule-hint'>
						Part of the bank’s description, any case. Pick a statement below, then cut it down to the part that stays the same.
					</span>
					<QuickPicks
						label='Statements'
						picks={statementPicks.map((entry) => ({ key: entry.value, text: entry.value, count: entry.count }))}
						onPick={(pick) => setStatement(pick.key)}
					/>
				</label>

				<label>
					<span className='grey-text'>Amount, exactly</span>
					<input
						type='text'
						inputMode='decimal'
						value={amountText}
						placeholder='Any amount'
						onChange={(event) => setAmountText(event.target.value)}
					/>
					<span className={(amount.error ? 'red-text' : 'grey-text') + ' import-rule-hint'}>
						{amount.error || 'In dollars, signed as Monarch shows it: a withdrawal is negative, e.g. -900.00.'}
					</span>
					<QuickPicks
						label='Amounts'
						picks={amountPicks.map((entry) => ({ key: entry.value, text: moneyFormatFromCents(entry.value), count: entry.count }))}
						onPick={(pick) => setAmountText(centsToDollars(pick.key))}
					/>
				</label>

				<RulePreview preview={preview} loading={loading} hasCondition={hasCondition} />

				<div className='import-rule-outcome-field'>
					<label className='grey-text' htmlFor={`${idPrefix}-outcome`}>These rows become</label>
					<div className='import-category-choice'>
						<CategorySelect
							inputId={`${idPrefix}-outcome`}
							aria-label='These rows become'
							value={choice}
							blank='Choose…'
							extraOptionGroups={EXTRA_OPTION_GROUPS}
							transactionName={merchant.trim() || undefined}
							onChange={(option) => setChoice(option ? option.value : RULE_NONE)}
						/>
						{!!assigned &&
						<div className='import-suggestion grey-text'>
							{assigned.group_name} &middot; {assigned.group_type_name}
						</div>
						}
					</div>
				</div>

				{signApplies &&
				<label className='import-rule-check'>
					<input
						type='checkbox'
						className='filled-in'
						checked={reverseSign}
						onChange={(event) => setReverseSign(event.target.checked)}
					/>
					<span>
						Reverse the sign
						<span className='grey-text import-rule-hint'>
							For a credit that is really a payment, like interest posted on a loan’s own account.
						</span>
					</span>
				</label>
				}
				{!!hint &&
				<div className='import-rule-sign-hint'>
					<span>{hint.text}</span>
					{hint.suggestReverse &&
					<Button small flat onClick={() => setReverseSign(true)}>Reverse the sign</Button>
					}
				</div>
				}

				<label className='import-rule-position-field'>
					<span className='grey-text'>Position</span>
					<input
						type='number'
						inputMode='numeric'
						min={1}
						max={rule ? ruleCount : ruleCount + 1}
						value={positionText}
						placeholder='Automatic'
						onChange={(event) => setPositionText(event.target.value)}
					/>
					<span className='grey-text import-rule-hint'>
						{rule
							? 'Where it sits in the list. 1 is checked first.'
							: 'Blank puts it just above the first rule that would catch every row it does, else last. '
							+ 'If a rule above that one still catches some of its rows, the list says so.'}
					</span>
				</label>
			</div>

			{!!error && <p className='red-text import-row-error'>{error}</p>}

			<div className='import-create-account-actions'>
				<Button small disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save rule'}</Button>
				<Button small flat disabled={saving} onClick={onCancel}>Cancel</Button>
			</div>
		</div>
	);
}

/**
 * rule_preview's answer for the draft's conditions, asked again once typing pauses. Answers that a
 * later draft has overtaken are dropped, and the last answer is kept on screen while the next is out,
 * so the pickers do not empty between keystrokes. The first is asked at once, for the pickers.
 */
function useRulePreview(params) {
	const key = JSON.stringify(params);
	const [state, setState] = useState({ preview: null, loading: true });
	const asked = useRef(false);

	useEffect(() => {
		let current = true;
		setState((before) => (before.loading ? before : { ...before, loading: true }));
		const timer = setTimeout(() => {
			Promise.resolve(fetchImportRulePreview(params))
				.then((preview) => {
					if(current) {
						setState({ preview, loading: false });
					}
				})
				.catch(() => {
					if(current) {
						setState({ preview: null, loading: false });
					}
				});
		}, asked.current ? PREVIEW_DELAY : 0);
		asked.current = true;
		return () => {
			current = false;
			clearTimeout(timer);
		};
	}, [key]);

	return state;
}

/** A few values to fill a typed field with in one tap, each with how many rows have it. */
function QuickPicks({ label, picks, onPick }) {
	if(!picks.length) {
		return null;
	}
	return (
		<span className='import-rule-picks' role='group' aria-label={label}>
			{picks.map((pick) => (
				<button
					key={pick.key}
					type='button'
					className='btn-flat import-rule-pick'
					onClick={(event) => {
						/** Inside the field's label, a click would otherwise go on to focus its input and nothing more. */
						event.preventDefault();
						onPick(pick);
					}}
				>
					{pick.text}
					<span className='grey-text'> &times;{pick.count}</span>
				</button>
			))}
		</span>
	);
}

/** How many fetched rows the draft matches, which way their money goes, and the newest few. */
function RulePreview({ preview, loading, hasCondition }) {
	if(!hasCondition) {
		return null;
	}
	let body;
	if(!preview) {
		body = <p className='grey-text'>{loading ? 'Checking which rows this matches…' : 'Could not check which rows this matches.'}</p>;
	} else if(!preview.row_count) {
		body = (
			<p>
				<strong>No row matches.</strong>{' '}
				<span className='grey-text'>
					Account, category, merchant and amount have to be exactly as Monarch writes them. Pick from the lists to be sure.
				</span>
			</p>
		);
	} else {
		const ways = [
			preview.debit_count ? `${preview.debit_count} money out` : null,
			preview.credit_count ? `${preview.credit_count} money in` : null,
		].filter(Boolean).join(', ');
		body = (
			<>
				<p>
					<strong>Matches {preview.row_count === 1 ? '1 row' : `${preview.row_count} rows`}</strong>
					<span className='grey-text'> imports have fetched{ways ? ` (${ways})` : ''}.</span>
				</p>
				<table className='import-rule-preview-rows'>
					<tbody>
						{preview.samples.map((row, index) => (
							<tr key={index}>
								<td className='grey-text'>{row.transaction_date}</td>
								<td>
									<div>{row.merchant || row.original_statement}</div>
									<div className='grey-text'>{[row.external_account, row.external_category].filter(Boolean).join(' · ')}</div>
								</td>
								<td className={row.amount > 0 ? 'green-text' : ''}>
									{row.amount === null ? '' : moneyFormatFromCents(row.amount)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
				{preview.row_count > preview.samples.length &&
				<p className='grey-text'>Newest {preview.samples.length} shown.</p>
				}
			</>
		);
	}
	return (
		<div className={`import-rule-preview ${loading ? 'import-rule-preview-loading' : ''}`.trim()} aria-live='polite'>
			{body}
		</div>
	);
}
